import { MAX_UPLOAD_FILE_SIZE_BYTES } from "@repo/app-types/constants/shared";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { IconFileUpload } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { useKnowledgeBaseDocumentsUploader } from "./useKnowledgeBaseDocumentsUploader.js";

interface KnowledgeBaseDocumentsUploaderProps {
  knowledgeBaseId: string;
  onUploadSuccess: (upload: { id: string; name: string }) => void;
  onUploadError: (fileName: string, error: any) => void;
  isDisabled?: boolean;
}

export const KnowledgeBaseDocumentsUploader: FC<
  KnowledgeBaseDocumentsUploaderProps
> = ({ knowledgeBaseId, onUploadSuccess, onUploadError, isDisabled }) => {
  const { dropzoneProps, isUploading } = useKnowledgeBaseDocumentsUploader({
    knowledgeBaseId,
    onUploadSuccess,
    onUploadError,
    isDisabled,
  });

  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    dropzoneProps;

  return (
    <div
      {...getRootProps()}
      className={twMerge(
        "group flex cursor-pointer justify-center rounded-lg border border-dashed border-border-strong px-5 py-5 hover:border-gray-400 hover:bg-gray-50",
        isDragAccept ? "border-green-500 bg-green-100" : "",
        isDragReject ? "border-red-500 bg-red-100" : "",
      )}
    >
      {isUploading ? (
        <div className="flex items-center">
          <Loader style={LoaderStyle.SPINNER} />
          <p className="ml-2 text-sm font-medium">Uploading document...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center">
          <IconFileUpload
            aria-hidden="true"
            className="mx-auto h-8 w-8 text-gray-300"
          />
          <div className="mt-3 flex text-center text-sm leading-6 text-muted-foreground">
            <label
              className="cursor-pointer font-semibold text-indigo-600 group-hover:text-indigo-500"
              htmlFor="file-upload"
            >
              <span>Upload</span>
              <input
                {...getInputProps()}
                className="sr-only"
                id="file-upload"
                name="file-upload"
                type="file"
              />
            </label>
            <p className="pl-1">or drag and drop documents.</p>
          </div>
          <p className="text-xs leading-5 text-muted-foreground">
            Up to {(MAX_UPLOAD_FILE_SIZE_BYTES / (1024 * 1024)).toFixed()} MB.
            PDF, DOC, DOCX, TXT, or MD.
          </p>
        </div>
      )}
    </div>
  );
};
